"use client";

import { useEffect, useMemo, useState } from "react";
import type { LifestylePrize } from "@/app/redux/features/api/lifestyle.types";
import { CloseIcon, SearchIcon } from "../lifestyleIcons";
import SearchSuggestions from "./SearchSuggestions";
import { buildSuggestions, type SearchSuggestion } from "./searchMatching";

interface SearchInputProps {
  value: string;
  /** Prizes the suggestions are drawn from, already scoped to the category. */
  suggestionPool: LifestylePrize[];
  onChange: (value: string) => void;
}

const LISTBOX_ID = "lifestyle-search-suggestions";
const optionId = (index: number) => `${LISTBOX_ID}-option-${index}`;

/**
 * Search with the autocomplete dropdown from the Angular page.
 *
 * `typed` is the single source of truth: it drives the suggestion list and the
 * debounced filter, while `activeIndex` only previews a suggestion in the box.
 * That mirrors MassAutoComplete, where arrowing wrote the highlighted value
 * into the field without applying it — nothing is committed until Enter or a
 * click, and index -1 is a real position you can arrow back to.
 */
export default function SearchInput({ value, suggestionPool, onChange }: SearchInputProps) {
  const [typed, setTyped] = useState(value);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  // Re-sync when the value changes upstream, e.g. URL hydration or Reset.
  useEffect(() => setTyped(value), [value]);

  // Debounced so filtering does not run on every keystroke. The URL write is
  // debounced separately in useLifestyleUrlState.
  useEffect(() => {
    if (typed === value) return;
    const timer = setTimeout(() => onChange(typed), 250);
    return () => clearTimeout(timer);
  }, [typed, value, onChange]);

  const suggestions = useMemo(() => buildSuggestions(suggestionPool, typed), [suggestionPool, typed]);
  const showDropdown = isOpen && suggestions.length > 0;
  const text = activeIndex >= 0 ? suggestions[activeIndex].value : typed;

  const commit = (term: string) => {
    setTyped(term);
    setActiveIndex(-1);
    setIsOpen(false);
    onChange(term);
  };

  /** Steps through the suggestions, wrapping via -1 so the typed term returns. */
  const move = (delta: number) => {
    if (suggestions.length === 0) return;

    if (!isOpen) {
      setIsOpen(true);
      setActiveIndex(delta > 0 ? 0 : suggestions.length - 1);
      return;
    }

    const positions = suggestions.length + 1;
    const next = (activeIndex + 1 + delta + positions) % positions;
    setActiveIndex(next - 1);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        move(1);
        break;
      case "ArrowUp":
        event.preventDefault();
        move(-1);
        break;
      case "Enter": {
        if (!showDropdown) return;
        event.preventDefault();
        // Enter with nothing highlighted takes the first suggestion, which is
        // the shortcut the Angular keyup handler added on top of the library.
        commit(suggestions[activeIndex >= 0 ? activeIndex : 0].value);
        break;
      }
      case "Escape":
        setIsOpen(false);
        setActiveIndex(-1);
        break;
      case "Tab":
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

  return (
    <div className="relative flex-1 min-w-0">
      {/* The border is the same divider tone at rest and on focus, as drawn;
          the caret is the only focus affordance in the design. */}
      <div className="flex items-center w-full min-h-10 lg:min-h-11 pl-4 pr-2 gap-2.5 bg-white border border-black/[0.08] rounded">
        <div className="flex items-center flex-1 min-w-0 gap-2">
          <SearchIcon className="shrink-0 size-5 text-gray-1" />
          <input
            type="text"
            role="combobox"
            autoComplete="off"
            aria-expanded={showDropdown}
            // Only while the list exists, so the IDREF never dangles.
            aria-controls={showDropdown ? LISTBOX_ID : undefined}
            aria-autocomplete="list"
            aria-activedescendant={activeIndex >= 0 ? optionId(activeIndex) : undefined}
            value={text}
            onChange={event => {
              setTyped(event.target.value);
              setActiveIndex(-1);
              setIsOpen(true);
            }}
            onFocus={() => setIsOpen(true)}
            onBlur={() => setIsOpen(false)}
            onKeyDown={onKeyDown}
            placeholder="Search"
            aria-label="Search prizes by make, model or style"
            // 16px at every breakpoint, as drawn, which also stops iOS zooming
            // on focus. The UA focus ring is dropped because the design gives
            // the field no focus treatment beyond the caret.
            className="flex-1 min-w-0 font-roboto text-base leading-5.5 text-gray-1 bg-transparent placeholder:text-gray-1 outline-none"
          />
        </div>
        {text !== "" && (
          <button
            type="button"
            onClick={() => commit("")}
            aria-label="Clear search"
            className="flex items-center justify-center shrink-0 size-6 text-gray-5 hover:text-dark-1 transition-colors">
            <CloseIcon className="size-6" />
          </button>
        )}
      </div>

      {showDropdown && (
        <SearchSuggestions
          id={LISTBOX_ID}
          suggestions={suggestions}
          activeIndex={activeIndex}
          term={typed}
          optionId={optionId}
          onSelect={(suggestion: SearchSuggestion) => commit(suggestion.value)}
        />
      )}
    </div>
  );
}
